import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppThunkDispatch } from 'state/store';

import {
  fetchMyTasks,
  selectFetchMyTasksStatus,
  selectMyTasks,
  selectMyTasksFilters,
  setFilters,
  setSearchFilter,
  setSorting,
  selectMyTasksTableSorting,
  selectCanFetchMoreMyTasks,
  selectMyTasksTotalCount,
  FetchMyTasksParams,
} from 'state/TasksList/myTasks/myTasksSlice';
import {
  setShouldRefreshTasks,
  selectShouldRefreshTasks,
} from 'state/TasksList/allTasks/allTasksSlice';

import {
  fetchMyTasks as fetchMyTasksNormalized,
  selectFetchMyTasksStatus as selectFetchMyTasksStatusNormalized,
  selectMyTasks as selectMyTasksNormalized,
  selectMyTasksFilters as selectMyTasksFiltersNormalized,
  setFilters as setFiltersNormalized,
  setSearchFilter as setSearchFilterNormalized,
  setSorting as setSortingNormalized,
  selectMyTasksTableSorting as selectMyTasksTableSortingNormalized,
  selectCanFetchMoreMyTasks as selectCanFetchMoreMyTasksNormalized,
  selectMyTasksTotalCount as selectMyTasksTotalCountNormalized,
} from 'state/normalized/tasksList/myTasks/myTasksSlice';
import {
  setShouldRefreshTasks as setShouldRefreshTasksNormalized,
  selectShouldRefreshTasks as selectShouldRefreshTasksNormalized,
} from 'state/normalized/tasksList/allTasks/allTasksSlice';

import { selectUserId } from 'state/User/userSlice';
import { Status } from 'utils/customTypes';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import { CENTRALIZED_TASKS_TABLE_TABS } from 'utils/constants/centralizedTasks';
import { SLICE_STATUS } from 'utils/constants';
import { TaskListFilters } from 'types/store/tasksList';

interface UseMyTasksProps {
  useNormalizedState?: boolean;
}

export const useMyTasks = ({ useNormalizedState = false }: UseMyTasksProps) => {
  const dispatch = useAppThunkDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const shouldRefreshTasks = useSelector(
    useNormalizedState
      ? selectShouldRefreshTasksNormalized
      : selectShouldRefreshTasks
  );
  const myTasks = useSelector(
    useNormalizedState ? selectMyTasksNormalized : selectMyTasks
  );
  const currentUserId = useSelector(selectUserId);
  const fetchMyTasksStatus = useSelector(
    useNormalizedState
      ? selectFetchMyTasksStatusNormalized
      : selectFetchMyTasksStatus
  );
  const myTasksFilters = useSelector(
    useNormalizedState ? selectMyTasksFiltersNormalized : selectMyTasksFilters
  );
  const myTasksSorting = useSelector(
    useNormalizedState
      ? selectMyTasksTableSortingNormalized
      : selectMyTasksTableSorting
  );
  const canFetchMoreTasks = useSelector(
    useNormalizedState
      ? selectCanFetchMoreMyTasksNormalized
      : selectCanFetchMoreMyTasks
  );
  const totalCount = useSelector(
    useNormalizedState
      ? selectMyTasksTotalCountNormalized
      : selectMyTasksTotalCount
  );

  const setShouldRefreshTasksAction = useMemo(
    () =>
      useNormalizedState
        ? setShouldRefreshTasksNormalized
        : setShouldRefreshTasks,
    [useNormalizedState]
  );

  const prevFetchMyTasksStatus = useRef<Status>(fetchMyTasksStatus);

  const dispatchFetchMyTasks = useCallback(
    (params: FetchMyTasksParams) => {
      const fetchMyTasksAction = useNormalizedState
        ? fetchMyTasksNormalized
        : fetchMyTasks;
      dispatch(fetchMyTasksAction(params));
    },
    [dispatch, useNormalizedState]
  );

  const init = useCallback(async () => {
    await dispatch(
      fetchFilterSettingByType(CENTRALIZED_TASKS_TABLE_TABS.MY_TASKS)
    );
    dispatchFetchMyTasks({
      userId: currentUserId!,
      fetchNext: false,
      includeTotalCount: true,
    });
  }, [dispatchFetchMyTasks, currentUserId, dispatch]);

  const refreshMyTasks = useCallback(() => {
    dispatch(setShouldRefreshTasksAction(true));
    init();
  }, [dispatch, init, setShouldRefreshTasksAction]);

  useEffect(() => {
    if (currentUserId) {
      init();
    }
  }, [init, currentUserId]);

  useEffect(() => {
    if (currentUserId && shouldRefreshTasks) {
      dispatch(setShouldRefreshTasksAction(false));
      init();
    }
  }, [
    currentUserId,
    shouldRefreshTasks,
    dispatch,
    init,
    setShouldRefreshTasksAction,
  ]);

  const hasError = fetchMyTasksStatus === SLICE_STATUS.FAILED;

  /**
   * Clear the initial loading state once the fetch reaches a terminal status.
   * FAILED must be treated as terminal alongside IDLE: myTasksSlice sets FAILED
   * (never IDLE) when fetchMyTasks rejects, so keying only off IDLE leaves
   * MyTasks rendering its skeleton loader forever on any request error.
   */
  useEffect(() => {
    if (fetchMyTasksStatus !== prevFetchMyTasksStatus.current) {
      prevFetchMyTasksStatus.current = fetchMyTasksStatus;
      if (
        isLoading &&
        (fetchMyTasksStatus === SLICE_STATUS.IDLE ||
          fetchMyTasksStatus === SLICE_STATUS.FAILED)
      ) {
        setIsLoading(false);
      }
    }
  }, [fetchMyTasksStatus, isLoading]);

  const handleUpdateFilters = useCallback(
    (updatedFilters: TaskListFilters) => {
      const setFiltersAction = useNormalizedState
        ? setFiltersNormalized
        : setFilters;
      dispatch(setFiltersAction(updatedFilters));
      dispatchFetchMyTasks({
        userId: currentUserId!,
        fetchNext: false,
        includeTotalCount: true,
      });
    },
    [dispatch, currentUserId, dispatchFetchMyTasks, useNormalizedState]
  );

  const handleSearchFilterChange = useCallback(
    (value: string) => {
      const setSearchFilterAction = useNormalizedState
        ? setSearchFilterNormalized
        : setSearchFilter;
      dispatch(setSearchFilterAction(value));
      dispatchFetchMyTasks({
        userId: currentUserId!,
        fetchNext: false,
        includeTotalCount: true,
      });
    },
    [dispatch, currentUserId, dispatchFetchMyTasks, useNormalizedState]
  );

  const handleTableSort = useCallback(
    (sortBy: string, order: string) => {
      const setSortingAction = useNormalizedState
        ? setSortingNormalized
        : setSorting;
      dispatch(setSortingAction({ sortBy, order }));
      dispatchFetchMyTasks({
        userId: currentUserId!,
        fetchNext: false,
        includeTotalCount: false,
      });
    },
    [dispatch, currentUserId, dispatchFetchMyTasks, useNormalizedState]
  );

  const handleFetchMoreTasks = useCallback(() => {
    dispatchFetchMyTasks({
      userId: currentUserId!,
      fetchNext: true,
      includeTotalCount: false,
    });
  }, [dispatchFetchMyTasks, currentUserId]);

  return useMemo(() => {
    return {
      isLoading,
      hasError,
      myTasks,
      myTasksFilters,
      myTasksSorting,
      canFetchMoreTasks,
      totalCount,
      currentUserId,
      refreshMyTasks,
      handleUpdateFilters,
      handleSearchFilterChange,
      handleTableSort,
      handleFetchMoreTasks,
    };
  }, [
    isLoading,
    hasError,
    myTasks,
    myTasksFilters,
    myTasksSorting,
    canFetchMoreTasks,
    totalCount,
    currentUserId,
    refreshMyTasks,
    handleUpdateFilters,
    handleSearchFilterChange,
    handleTableSort,
    handleFetchMoreTasks,
  ]);
};
